import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import {
  useMainFrameDispatch,
  useMainFrameState,
} from '../../../../component/MainUIFrame/MainFrame';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import {
  ACTION_CLOSE_RIGHT_FLOATING_DRAWER,
  ACTION_OPEN_RIGHT_FLOATING_DRAWER,
} from '../../../../component/MainUIFrame/constants';
import dayjs from 'dayjs';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LoadingButton } from '@mui/lab';
import { ehrAPIBaseService, ehrAPIService } from '../../../../http-common';
import { generateNotifications } from '../../../../component/reusableLogic';
import { useSnackbar } from 'notistack';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const ExportPatientDialog = props => {
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      format: 'json',
      include: includeItemList
        .map(f =>
          f?.value === 'documents' && props?.multipleRowSelected?.length > 1
            ? ''
            : f?.value
        )
        .filter(f => f),
      archivePassword: '',
      dates: [],
    },
  });
  const { isRightDrawerOpen } = useMainFrameState();
  const dispatch = useMainFrameDispatch();
  const historySubItemsRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmationPassword, setShowConfirmationPassword] =
    useState(false);

  const reduxDispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const FormError = ({ errors }) => {
    return (
      <Typography variant="caption" color="error">
        {errors}
      </Typography>
    );
  };

  const onSubmit = async data => {
    try {
      setLoading(true);
      let params = {
        // searchParams: {
        //   resourceType: 'Patient',
        //   _id: props?.multipleRowSelected
        //     ?.map(e => e?.resource?.id)
        //     .filter(f => f)
        //     .join(','),
        // },
        archivePassword: data?.archivePassword,
        dateRange: {
          start: dayjs(data?.dates?.encounters?.startDate).format('YYYY-MM-DD'),
          end: dayjs(data?.dates?.encounters?.endDate).format('YYYY-MM-DD'),
        },
        name: data?.name || '',
        resourcesToExport: [
          ...data?.include,
          ...historySubItems.filter(f => f?.selected).map(e => e?.value),
        ],
      };
      if (!data?.dates?.encounters?.startDate) delete params.dateRange.start;
      if (!data?.dates?.encounters?.endDate) delete params.dateRange.end;
      let response = await ehrAPIService.post(
        `/exportPatient/${props?.data?.resource?.id}`,
        params
      );
      if (response?.status === 200) {
        generateNotifications({
          title: 'Success!',
          descriptions:
            'Patient export request has been processed successfully, it will be available for download shortly.',
          enqueueSnackbar,
          closeSnackbar,
          reduxDispatch,
        });
        dispatch({ type: ACTION_CLOSE_RIGHT_FLOATING_DRAWER });
      } else {
        generateNotifications({
          title: 'Opps!',
          descriptions:
            response?.data?.message ||
            'Something went wrong, please try again later.',
          enqueueSnackbar,
          closeSnackbar,
          reduxDispatch,
        });
      }
    } catch (e) {
      generateNotifications({
        title: 'Opps!',
        descriptions:
          e?.message || 'Something went wrong, please try again later.',
        enqueueSnackbar,
        closeSnackbar,
        reduxDispatch,
      });
    } finally {
      setLoading(false);
    }
  };

  const [historySubItems, setHistorySubItems] = useState([
    {
      label: 'Past Medical History',
      value: 'past_medical_history',
      selected: true,
    },
    { label: 'Social History', value: 'social_history', selected: true },
    {
      label: 'Family Member History',
      value: 'family_member_history',
      selected: true,
    },
    {
      label: 'Immunization History',
      value: 'immunization_history',
      selected: true,
    },
    { label: 'Menstrual History', value: 'menstrual_history', selected: true },
    { label: 'Pregnancy History', value: 'pregnancy_history', selected: true },
    { label: 'Surgical History', value: 'surgical_history', selected: true },
  ]);

  // useEffect(() => {
  //   if (historySubItemsRef?.current) {
  //     historySubItemsRef?.current?.scrollIntoView({ behavior: 'smooth' });
  //   }
  // }, [watch('include')?.includes('history')]);

  return (
    <>
      <Grid
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 60px)',
        }}
      >
        <Grid sx={{ flex: '1', overflow: 'auto', paddingBottom: '10px' }}>
          <Grid
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
            container
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: isRightDrawerOpen ? 'flex-start' : 'center',
                alignItems: 'center',
              }}
            >
              {isRightDrawerOpen ? (
                <IconButton
                  onClick={() => {
                    dispatch({ type: ACTION_CLOSE_RIGHT_FLOATING_DRAWER });
                  }}
                >
                  <Icon>chevron_right</Icon>
                </IconButton>
              ) : (
                <IconButton
                  onClick={() => {
                    dispatch({ type: ACTION_OPEN_RIGHT_FLOATING_DRAWER });
                  }}
                >
                  <Icon>chevron_left</Icon>
                </IconButton>
              )}
            </Box>
            <Typography color="primary" px={2} fontSize={20}>
              Export
            </Typography>
          </Grid>
          <Grid container px={2} spacing={1} mt={1}>
            <Grid item sm={12}>
              <Typography sx={{ fontSize: 14 }}>
                Job Name <font color="red">*</font>
              </Typography>
              <Controller
                name={'name'}
                control={control}
                rules={{
                  required: { value: true, message: 'Job Name is required' },
                  maxLength: {
                    value: 50,
                    message: 'Job name not contain more than 50 characters',
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <>
                    <TextField
                      size="small"
                      fullWidth
                      type="text"
                      onChange={e => onChange(e.target.value)}
                      value={value}
                      error={Boolean(errors.name)}
                    />
                  </>
                )}
              />
              {errors?.name && <FormError errors={errors?.name?.message} />}
            </Grid>
            <Grid item sm={12}>
              <Typography sx={{ fontSize: 14 }}>
                Format <font color="red">*</font>
              </Typography>
              <Controller
                name="format"
                control={control}
                rules={{
                  required: 'Format is required',
                }}
                render={({ field }) => (
                  <>
                    <Select
                      fullWidth
                      name="format"
                      key={field?.value}
                      value={field?.value}
                      onChange={e => field?.onChange(e.target.value)}
                      size="small"
                      error={errors.format}
                    >
                      {/* <MenuItem value="ccda">CCDA</MenuItem> */}
                      {/* <MenuItem value="xml">XML</MenuItem> */}
                      <MenuItem value="json">JSON</MenuItem>
                      {/* <MenuItem value="fhir">FHIR</MenuItem> */}
                    </Select>
                    <FormError
                      errors={errors.format && errors.format?.message}
                    />
                  </>
                )}
              />
            </Grid>
            {/* <Grid item sm={12}>
              <Typography sx={{ fontSize: 14 }}>Start Date</Typography>
              <Controller
                name={`startDate`}
                control={control}
                rules={{
                  validate: {
                    validAndMinDate: value => {
                      const date = dayjs(value);
                      const isValid =
                        date?.isValid() && date?.isAfter(dayjs('1900-01-01'));
                      return (
                        isValid ||
                        'Invalid date. The date should be in the format MM/DD/YYYY and after 01/01/1900'
                      );
                    },
                  },
                }}
                render={({ field }) => (
                  <>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DesktopDatePicker
                        inputFormat="MM/dd/yyyy"
                        name={`startDate`}
                        // maxDate={dayjs()}
                        value={field?.value || null}
                        minDate={new Date('1900-01-01')}
                        onChange={date => {
                          field?.onChange(date);
                        }}
                        renderInput={params => (
                          <TextField
                            fullWidth
                            size="small"
                            {...params}
                            error={errors?.startDate}
                          />
                        )}
                      />
                    </LocalizationProvider>
                    <FormError
                      errors={errors?.startDate && errors?.startDate?.message}
                    />
                  </>
                )}
              />
            </Grid> */}
            {/* <Grid item sm={12}>
              <Typography sx={{ fontSize: 14 }}>
                End Date
              </Typography>
              <Controller
                name={`endDate`}
                control={control}
                rules={{
                  // required: 'End Date is required',
                  validate: {
                    validAndMinDate: value => {
                      const date = dayjs(value);
                      const isValid =
                        date?.isValid() && date?.isAfter(dayjs('1900-01-01'));
                      return (
                        isValid ||
                        'Invalid date. The date should be in the format MM/DD/YYYY and after 01/01/1900'
                      );
                    },
                    // futureDate: value =>
                    //   isFutureDate(value) || 'Future Date is not allowed',
                  },
                }}
                render={({ field }) => (
                  <>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DesktopDatePicker
                        inputFormat="MM/dd/yyyy"
                        name={`endDate`}
                        // maxDate={dayjs()}
                        value={field?.value || null}
                        // open={open}
                        // onOpen={() => setOpen(true)}
                        // onClose={() => setOpen(false)}
                        // error={errors.lastMenstrualPeriod}
                        minDate={new Date('1900-01-01')}
                        onChange={date => {
                          field?.onChange(date);
                          // setOpen(false);
                        }}
                        renderInput={params => (
                          <TextField
                            fullWidth
                            size="small"
                            {...params}
                            error={errors?.endDate}
                          // onClick={() => setOpen(true)}
                          />
                        )}
                      />
                    </LocalizationProvider>
                    <FormError
                      errors={errors?.endDate && errors?.endDate?.message}
                    />
                  </>
                )}
              />
            </Grid> */}
            <Grid item sm={12}>
              <Typography sx={{ fontSize: 14 }}>
                Password <font color="red">*</font>
              </Typography>
              <Controller
                name={'archivePassword'}
                control={control}
                rules={{
                  required: { value: true, message: 'Password is required' },
                  maxLength: {
                    value: 16,
                    message:
                      'Password should not contain more than 16 characters',
                  },
                  minLength: {
                    value: 8,
                    message:
                      'Password should not contain less than 8 characters',
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <>
                    <TextField
                      size="small"
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      onChange={e => onChange(e.target.value)}
                      value={value}
                      error={Boolean(errors.archivePassword)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </>
                )}
              />
              {errors?.archivePassword && (
                <FormError errors={errors?.archivePassword?.message} />
              )}
            </Grid>
            <Grid item sm={12}>
              <Typography sx={{ fontSize: 14 }}>
                Confirm Password <font color="red">*</font>
              </Typography>
              <Controller
                name={'confirmPassword'}
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Confirmation Password is required',
                  },
                  maxLength: {
                    value: 16,
                    message:
                      'Password should not contain more than 16 characters',
                  },
                  minLength: {
                    value: 8,
                    message:
                      'Password should not contain less than 8 characters',
                  },
                  validate: val => {
                    if (watch('archivePassword') != val) {
                      return 'Your passwords do not match';
                    }
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <>
                    <TextField
                      size="small"
                      fullWidth
                      type={showConfirmationPassword ? 'text' : 'password'}
                      onChange={e => onChange(e.target.value)}
                      value={value}
                      error={Boolean(errors.confirmPassword)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmationPassword(
                                  !showConfirmationPassword
                                )
                              }
                              edge="end"
                            >
                              {showConfirmationPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </>
                )}
              />
              {errors?.confirmPassword && (
                <FormError errors={errors?.confirmPassword?.message} />
              )}
            </Grid>
            <Grid item sm={12}>
              <Typography sx={{ fontSize: 14 }}>
                Modules <font color="red">*</font>
              </Typography>
              <Divider />
              <Controller
                name="include"
                control={control}
                render={({ field }) => (
                  <>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="include"
                            checked={
                              getValues('include').length ===
                              includeItemList.length
                            }
                            indeterminate={
                              getValues('include').length === 0
                                ? false
                                : getValues('include').length !==
                                  includeItemList.length
                            }
                            onChange={e => {
                              if (e?.target?.checked) {
                                setValue('include', [
                                  ...includeItemList.map(m => m.value),
                                ]);
                                setHistorySubItems(
                                  historySubItems?.map(e => {
                                    return {
                                      ...e,
                                      selected: true,
                                    };
                                  })
                                );
                              } else {
                                setValue('include', []);
                                setHistorySubItems(
                                  historySubItems?.map(e => {
                                    return {
                                      ...e,
                                      selected: false,
                                    };
                                  })
                                );
                              }
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="h6"
                            style={{ fontWeight: 'bolder', fontSize: '12pt' }}
                          >
                            {getValues('include').length ===
                            includeItemList.length
                              ? 'Unselect All'
                              : 'Select All'}
                          </Typography>
                        }
                      />
                      <div
                        style={{
                          marginLeft: '10px',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {includeItemList.map((item, index) => (
                          <>
                            <FormControlLabel
                              key={index}
                              style={{ width: 'fit-content' }}
                              control={
                                <Checkbox
                                  name="include"
                                  checked={field?.value?.includes(item?.value)}
                                  disabled={
                                    item?.value === 'documents' &&
                                    props?.multipleRowSelected?.length > 1
                                  }
                                  onChange={e => {
                                    if (e?.target?.checked) {
                                      field?.onChange([
                                        ...(field?.value || []),
                                        item?.value,
                                      ]);
                                      if (item?.value === 'history') {
                                        setHistorySubItems(
                                          historySubItems?.map(e => {
                                            return {
                                              ...e,
                                              selected: true,
                                            };
                                          })
                                        );
                                      }
                                    } else {
                                      field?.onChange(
                                        (field?.value || [])?.filter(
                                          val => val !== item.value
                                        )
                                      );
                                      if (item?.value === 'history') {
                                        setHistorySubItems(
                                          historySubItems?.map(e => {
                                            return {
                                              ...e,
                                              selected: false,
                                            };
                                          })
                                        );
                                      }
                                    }
                                  }}
                                />
                              }
                              label={item.label}
                            />
                            {field?.value?.includes(item?.value) &&
                              (item?.dateRange ? (
                                <>
                                  <Box ml={2}>
                                    <Typography sx={{ fontSize: 14 }}>
                                      Start Date
                                    </Typography>
                                    <Controller
                                      name={`dates.${item?.value}.startDate`}
                                      control={control}
                                      rules={{
                                        validate: {
                                          validAndMinDate: value => {
                                            const date = dayjs(value);
                                            if (value !== null) {
                                              if (
                                                !date?.isValid() &&
                                                value !== null
                                              ) {
                                                const isValid = date?.isAfter(
                                                  dayjs('1900-01-01')
                                                );
                                                return (
                                                  isValid ||
                                                  'Invalid date. The date should be in the format MM/DD/YYYY and after 01/01/1900'
                                                );
                                              }
                                            }
                                          },
                                        },
                                      }}
                                      render={({ field }) => (
                                        <>
                                          <LocalizationProvider
                                            dateAdapter={AdapterDateFns}
                                          >
                                            <DesktopDatePicker
                                              inputFormat="MM/dd/yyyy"
                                              name={`dates.${item?.value}.startDate`}
                                              value={field?.value || null}
                                              minDate={new Date('1900-01-01')}
                                              onChange={date => {
                                                field?.onChange(date);
                                              }}
                                              renderInput={params => (
                                                <TextField
                                                  fullWidth
                                                  size="small"
                                                  {...params}
                                                  error={
                                                    errors?.dates?.[item?.value]
                                                      ?.startDate
                                                  }
                                                />
                                              )}
                                            />
                                          </LocalizationProvider>
                                          <FormError
                                            errors={
                                              errors?.dates?.[item?.value]
                                                ?.startDate &&
                                              errors?.dates?.[item?.value]
                                                ?.startDate?.message
                                            }
                                          />
                                        </>
                                      )}
                                    />
                                  </Box>
                                  <Box ml={2}>
                                    <Typography sx={{ fontSize: 14 }}>
                                      End Date
                                    </Typography>
                                    <Controller
                                      name={`dates.${item?.value}.endDate`}
                                      control={control}
                                      rules={{
                                        validate: {
                                          validAndMinDate: value => {
                                            const date = dayjs(value);
                                            if (
                                              getValues(
                                                `dates.${item?.value}.startDate`
                                              )
                                            ) {
                                              if (value !== null) {
                                                if (date?.isValid()) {
                                                  const isValid =
                                                    date?.isAfter(
                                                      dayjs(
                                                        getValues(
                                                          `dates.${item?.value}.startDate`
                                                        )
                                                      )
                                                    ) ||
                                                    date?.isSame(
                                                      dayjs(
                                                        getValues(
                                                          `dates.${item?.value}.startDate`
                                                        )
                                                      )
                                                    );
                                                  return (
                                                    isValid ||
                                                    'The end date must follow the start date and cannot precede it.'
                                                  );
                                                } else return 'Invalid Date';
                                              }
                                            }
                                          },
                                        },
                                      }}
                                      render={({ field }) => (
                                        <>
                                          <LocalizationProvider
                                            dateAdapter={AdapterDateFns}
                                          >
                                            <DesktopDatePicker
                                              inputFormat="MM/dd/yyyy"
                                              name={`dates.${item?.value}.endDate`}
                                              value={field?.value || null}
                                              minDate={new Date('1900-01-01')}
                                              onChange={date => {
                                                field?.onChange(date);
                                              }}
                                              renderInput={params => (
                                                <TextField
                                                  fullWidth
                                                  size="small"
                                                  {...params}
                                                  error={
                                                    errors?.dates?.[item?.value]
                                                      ?.endDate
                                                  }
                                                />
                                              )}
                                            />
                                          </LocalizationProvider>
                                          <FormError
                                            errors={
                                              errors?.dates?.[item?.value]
                                                ?.endDate &&
                                              errors?.dates?.[item?.value]
                                                ?.endDate?.message
                                            }
                                          />
                                        </>
                                      )}
                                    />
                                  </Box>
                                </>
                              ) : (
                                item?.mulipleHistories && (
                                  <Box
                                    sx={{ marginLeft: '20px' }}
                                    ref={historySubItemsRef}
                                  >
                                    {historySubItems?.map(
                                      (subItem, subIndex) => (
                                        <Box>
                                          <FormControlLabel
                                            key={subIndex}
                                            control={
                                              <Checkbox
                                                name={subItem?.value}
                                                checked={subItem?.selected}
                                                // disabled
                                                size="small"
                                                onChange={e => {
                                                  setHistorySubItems(
                                                    historySubItems.map(
                                                      (ele, ind) => {
                                                        if (ind === subIndex) {
                                                          return {
                                                            ...ele,
                                                            selected:
                                                              e?.target
                                                                ?.checked,
                                                          };
                                                        }
                                                        return ele;
                                                      }
                                                    )
                                                  );
                                                }}
                                              />
                                            }
                                            label={
                                              <Typography
                                                sx={{
                                                  fontSize: '14px',
                                                }}
                                              >
                                                {subItem?.label}
                                              </Typography>
                                            }
                                          />
                                        </Box>
                                      )
                                    )}
                                  </Box>
                                )
                              ))}
                          </>
                        ))}
                      </div>
                    </FormGroup>
                  </>
                )}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid sx={{ marginBottom: '-50px' }} py={1}>
          <Grid container px={2} spacing={1}>
            <Grid item sm={12}>
              <LoadingButton
                name="updateAnnotations"
                onClick={handleSubmit(onSubmit)}
                variant="contained"
                fullWidth
                loading={loading}
              >
                Export
              </LoadingButton>
            </Grid>
            <Grid item sm={12}>
              <Button
                onClick={() => {
                  dispatch({ type: ACTION_CLOSE_RIGHT_FLOATING_DRAWER });
                }}
                variant="outlined"
                fullWidth
              >
                Close
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default ExportPatientDialog;

const includeItemList = [
  // { label: 'Patient Profile', value: 'patientProfile' },
  { label: 'Vitals', value: 'vitals' },
  { label: 'Allergies', value: 'allergies' },
  { label: 'Encounters', value: 'encounters', dateRange: true }, //, dateRange: true
  { label: 'Problems', value: 'problems' },
  // { label: 'Labs', value: 'labs' },
  { label: 'Coverages', value: 'coverages' },
  { label: 'Implanted Devices', value: 'implantedDevices' },
  { label: 'Medications', value: 'medications' },
  { label: 'Documents', value: 'documents' },
  { label: 'History', value: 'history', mulipleHistories: true },
];
